import {useState} from "react";
import {
    TokenCredentialAuthenticationProvider
} from "@microsoft/microsoft-graph-client/lib/src/authentication/azureTokenCredentials";
import {Client} from "@microsoft/microsoft-graph-client";
import {TeamsUserCredential} from "@microsoft/teamsfx";

export function useGraphClient(
    teamsUserCredential: TeamsUserCredential | undefined,
    requiredScopes: string[]
): {
    client: Client | undefined,
    error: string | null,
    loading: boolean,
    needsConsent: boolean,
    getGraphClient: () => Promise<Client | undefined>,
    requestConsent: () => Promise<boolean>,
} {
    const [client, setClient] = useState<Client | undefined>(undefined);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [needsConsent, setNeedsConsent] = useState<boolean>(false);

    /**
     * Explicitly requests user consent by triggering the login flow
     */
    const requestConsent = async (): Promise<boolean> => {
        if (!teamsUserCredential) {
            setError("No user credentials available");
            return false;
        }

        try {
            setLoading(true);
            await teamsUserCredential.login(requiredScopes);
            setNeedsConsent(false);
            return true;
        } catch (error: any) {
            setError("Failed to get consent");
            return false;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Gets or creates a Microsoft Graph client, handling consent if needed
     */
    const getGraphClient = async (): Promise<Client | undefined> => {
        if (!teamsUserCredential) {
            setError("No user credentials");
            return undefined;
        }

        setLoading(true);

        try {
            try {
                await teamsUserCredential.getToken(requiredScopes);
            } catch (tokenError: any) {
                console.log("Silent token acquisition failed:", tokenError);
                throw tokenError;
            }

            // If we got here, we have a token, create the auth provider
            const authProvider = new TokenCredentialAuthenticationProvider(teamsUserCredential, {
                scopes: requiredScopes
            });

            const graphClient = Client.initWithMiddleware({
                authProvider: authProvider
            });

            setClient(graphClient);
            setError(null);
            setNeedsConsent(false);
            setLoading(false);
            return graphClient;
        } catch (error: any) {
            const isConsentError =
                error.message?.includes("login first") ||
                error.message?.includes("consent") ||
                error.message?.includes("permission") ||
                error.message?.includes("get access token") ||
                error.statusCode === 401;

            if (isConsentError) {
                setNeedsConsent(true);
                setError("This application needs additional permissions.");
            } else {
                setError("Failed to initialize Graph client: " + error.message);
            }

            setLoading(false);
            return undefined;
        }
    };

    return {
        client,
        error,
        loading,
        needsConsent,
        getGraphClient,
        requestConsent
    };
}
